import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/videoData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-play-circle-fill text-background-50 text-xl"></i>
            </div>
            <span className={`font-heading text-xl ${scrolled || !isHome ? 'text-foreground-900' : 'text-background-50'}`}>
              爱液视频
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? scrolled || !isHome ? 'bg-primary-100 text-primary-700' : 'bg-background-50/20 text-background-50'
                  : scrolled || !isHome ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                  scrolled || !isHome ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50'
                }`}
              >
                分类浏览
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {categoryOpen && (
                <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-xl border border-background-200/70 py-2">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-lg">
                        <i className={`${cat.icon} text-secondary-600`}></i>
                      </div>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? scrolled || !isHome ? 'bg-primary-100 text-primary-700' : 'bg-background-50/20 text-background-50'
                  : scrolled || !isHome ? 'text-foreground-700 hover:bg-background-100' : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className={`relative ${scrolled || !isHome ? '' : 'text-background-50'}`}>
              <input
                type="text"
                placeholder="搜索视频..."
                className={`pl-9 pr-4 py-2 rounded-full text-sm border outline-none transition-colors w-48 focus:w-64 ${
                  scrolled || !isHome
                    ? 'bg-background-100 border-background-200 text-foreground-900 placeholder-foreground-400 focus:border-primary-300'
                    : 'bg-background-50/15 border-background-50/20 text-background-50 placeholder-background-50/50 focus:border-background-50/40'
                }`}
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm"></i>
            </div>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
              scrolled || !isHome ? 'text-foreground-700' : 'text-background-50'
            }`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-4 px-4">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                首页
              </Link>
              <div className="px-4 py-3">
                <p className="text-xs text-foreground-400 mb-2 font-medium">视频分类</p>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100"
                    >
                      <i className={`${cat.icon} text-sm`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
              <Link
                to="/about"
                className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100"
              >
                关于我们
              </Link>
              <div className="px-4 pt-2">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="搜索视频..."
                    className="pl-9 pr-4 py-2.5 rounded-full text-sm border border-background-200 bg-background-100 outline-none w-full text-foreground-900 placeholder-foreground-400 focus:border-primary-300"
                  />
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}